import dayjs from 'dayjs';

import { INCIDENT_BANNER_DISMISSED_KEY } from '@/constants/expiredBannerConstants';
import { Button, Text } from '@/design-system';
import {
  Impact,
  MaintenanceStatus,
  STATUS_PAGE_URL,
  Status,
  useIncidentStatus,
} from '@/hooks/useIncidentStatus';
import { isSelfHosted } from '@/utils/is-self-hosted';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import Banner from '../Banner';

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('MMM D, YYYY [at] h:mm A');
};

const generateMaintenanceKey = (
  maintenanceId: string,
  status: MaintenanceStatus
): string => {
  return `maintenance:${maintenanceId}:${status}`;
};

const generateIncidentKey = (incidentId: string): string => {
  return `incident:${incidentId}`;
};

export const IncidentBanner = () => {
  const { data } = useIncidentStatus();

  const [dismissedKeys, setDismissedKeys] = useLocalStorageState<string[]>(
    INCIDENT_BANNER_DISMISSED_KEY,
    []
  );

  const ongoingIncident = data?.ongoing_incidents?.find((incident) => {
    if (
      incident.status === Status.RESOLVED ||
      incident.status === Status.MONITORING ||
      incident.current_worst_impact === Impact.OPERATIONAL
    ) {
      return false;
    }
    const key = generateIncidentKey(incident.id);
    return !dismissedKeys.includes(key);
  });

  const ongoingMaintenance = data?.in_progress_maintenances?.find(
    (maintenance) => {
      const key = generateMaintenanceKey(maintenance.id, maintenance.status);
      return !dismissedKeys.includes(key);
    }
  );

  const scheduledMaintenance = data?.scheduled_maintenances?.find(
    (maintenance) => {
      const key = generateMaintenanceKey(maintenance.id, maintenance.status);
      return !dismissedKeys.includes(key);
    }
  );

  const hasOngoingIncident = !!ongoingIncident;
  const hasOngoingMaintenance = !!ongoingMaintenance;
  const hasScheduledMaintenance = !!scheduledMaintenance;

  const showBanner =
    hasOngoingIncident || hasOngoingMaintenance || hasScheduledMaintenance;

  if (!showBanner || isSelfHosted) {
    return null;
  }

  const getTitle = () => {
    if (hasOngoingIncident) {
      return "We're currently experiencing issues";
    }
    if (hasOngoingMaintenance) {
      return 'Maintenance in progress';
    }
    return 'Scheduled maintenance';
  };

  const getDescription = () => {
    const suffix = ' For more information, see our status page ';
    if (hasOngoingIncident) {
      return 'Services may be degraded or unavailable.' + suffix;
    }
    if (hasOngoingMaintenance) {
      return (
        "We're currently performing scheduled maintenance. Some services may be temporarily unavailable." +
        suffix
      );
    }
    if (hasScheduledMaintenance && scheduledMaintenance) {
      const startsAt = scheduledMaintenance.starts_at
        ? formatDate(scheduledMaintenance.starts_at)
        : '';
      const endsAt = scheduledMaintenance.ends_at
        ? formatDate(scheduledMaintenance.ends_at)
        : '';

      if (startsAt && endsAt) {
        return (
          `We have scheduled maintenance from ${startsAt} to ${endsAt} that may affect service availability.` +
          suffix
        );
      } else if (startsAt) {
        return (
          `We have scheduled maintenance starting ${startsAt} that may affect service availability.` +
          suffix
        );
      }
      return (
        'We have scheduled maintenance that may affect service availability.' +
        suffix
      );
    }
  };

  const getIntent = () => {
    if (hasOngoingIncident || hasOngoingMaintenance) {
      return 'warning' as const;
    }
    return 'info' as const;
  };

  const handleDismissBanner = () => {
    if (hasOngoingIncident) {
      const key = generateIncidentKey(ongoingIncident.id);
      setDismissedKeys((prev) => [...prev, key]);
      return;
    }
    if (hasOngoingMaintenance) {
      const key = generateMaintenanceKey(
        ongoingMaintenance.id,
        ongoingMaintenance.status
      );
      setDismissedKeys((prev) => [...prev, key]);
      return;
    }
    if (hasScheduledMaintenance) {
      const key = generateMaintenanceKey(
        scheduledMaintenance.id,
        scheduledMaintenance.status
      );
      setDismissedKeys((prev) => [...prev, key]);
      return;
    }
  };

  const getStatusPageUrl = () => {
    if (hasOngoingIncident && ongoingIncident?.url) {
      return ongoingIncident.url;
    }
    if (hasOngoingMaintenance && ongoingMaintenance?.url) {
      return ongoingMaintenance.url;
    }
    if (hasScheduledMaintenance && scheduledMaintenance?.url) {
      return scheduledMaintenance.url;
    }
    // Return the global status page link for to support concurrent incidents.
    return STATUS_PAGE_URL;
  };

  return (
    <Banner
      open={true}
      closeable={true}
      intent={getIntent()}
      title={getTitle()}
      className="m-4 mb-0"
      onClose={handleDismissBanner}
      description={
        <div className="flex items-center">
          <Text variant="sm">{getDescription()}</Text>
          <a
            href={getStatusPageUrl()}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="underlined" className="ml-1 mt-0.5 bg-transparent">
              here
            </Button>
          </a>
          .
        </div>
      }
      shadow={false}
    />
  );
};
